import debounce from "lodash.debounce";

import { scroll } from "@utilities";

/**
 * Scroll panel is handled by horizontalScrollPanel script
 * but some functionality depends on events from
 * actual components (Tabs, Comparator), so it is important
 * to call "handleActivePanelButtonVisibility" when and ifyou need it
 */
import { handleActivePanelButtonVisibility } from "./horizontalScrollPanel";

const CLASS_PREFIX = "js-tabs";

const ACTIVE_CLASS = "is-active";
const HIDDEN_CLASS = "is-hidden";
const TABLIST_INITIAL_CLASS = "initial-setup";

const HEIGHT_ANIMATION_DURATION_MS = 250;

/**
 * Handle tabs behaviour
 *
 * @param container {HTMLElement} container element
 */
const Tabs = (container: HTMLElement): void => {
  let previousActiveTabIndex = 0;
  let activeTabIndex = 0;
  let reqFrameId = 0;
  // check initial load to remove inline height from contentOuterContainer
  let initialLoad = true;
  // Helper variable - if switching is active, skip transitionend listeners
  let isSwitchingActive = false;

  const tabButtonListEl: HTMLUListElement = container.querySelector(`.${CLASS_PREFIX}-button-list`);

  // Node list of tab buttons
  const tabButtonArr: NodeListOf<HTMLAnchorElement> = tabButtonListEl.querySelectorAll(
    `.${CLASS_PREFIX}-button`
  );
  // Outer container for tab height transition - common for every tab
  const contentOuterContainer: HTMLDivElement = container.querySelector(
    `.${CLASS_PREFIX}-content-outer-container`
  );
  // Node list of content containers - used for changing class for visibility
  const tabContentContainerArr: NodeListOf<HTMLDivElement> = container.querySelectorAll(
    `.${CLASS_PREFIX}-content-container`
  );
  // Node list of contents itself - used for opacity transition
  const tabContentArr: NodeListOf<HTMLDivElement> = container.querySelectorAll(
    `.${CLASS_PREFIX}-content`
  );

  /**
   * Handle class switch on new and old content container
   */
  const showNewTabContent = ({
    newActiveContentContainer,
  }: {
    newActiveContentContainer: HTMLDivElement;
  }): void => {
    newActiveContentContainer.addEventListener(
      "transitionend",
      () => {
        isSwitchingActive = false;
      },
      { once: true }
    );

    newActiveContentContainer.classList.remove(HIDDEN_CLASS);
    newActiveContentContainer.classList.add(ACTIVE_CLASS);

    if (initialLoad) {
      initialLoad = false;

      if (contentOuterContainer) {
        contentOuterContainer.removeAttribute("style");
      }
    }

    /**
     * Find every non active content container without HIDDEN_CLASS.
     */
    const notHiddenInactiveContainers = Array.from(tabContentContainerArr).filter(
      (tabContainer) => {
        if (tabContainer === newActiveContentContainer) return false;

        return !tabContainer.classList.contains(HIDDEN_CLASS);
      }
    );

    /**
     * Hide every non active container without HIDDEN_CLASS.
     *
     * This is for making sure, that they will not take any space in the DOM,
     * in case user will be switching tabs too fast and transitions will not be
     * finished on time before next transition happens.
     */
    notHiddenInactiveContainers.forEach((containerToHide) => {
      containerToHide.classList.add(HIDDEN_CLASS);
    });
  };

  /**
   * Handle content height animation.
   * If window is resized and tab is not switched for another one - do not change classes
   *
   */
  const handleContentSwitch = ({
    isWindowResized = false,
    additionalHeight = 0,
  }: {
    isWindowResized?: boolean;
    additionalHeight?: number;
  }): void => {
    if (reqFrameId) window.cancelAnimationFrame(reqFrameId);

    const previousActiveContentContainer = tabContentContainerArr[previousActiveTabIndex];
    const newActiveContentContainer = tabContentContainerArr[activeTabIndex];

    const newActiveContent = tabContentArr[activeTabIndex];

    const { height: currentHeight } = contentOuterContainer.getBoundingClientRect();
    const newHeight = newActiveContent.scrollHeight + additionalHeight;

    let startTime = 0;
    const heightDifference = newHeight - currentHeight;

    // Request Animation Frame callback
    const animateContentHeight = (timestamp: number) => {
      if (!startTime) startTime = timestamp;

      const runTime = timestamp - startTime;
      const relativeProgress = runTime / HEIGHT_ANIMATION_DURATION_MS;

      /**
       * Calculate height for new frame animation (always starting from current height)
       *
       * It is possible that "heightDifference" is negative, which is necessary
       * in cases when content container is shrinking
       */
      const height = currentHeight + heightDifference * Math.min(relativeProgress, 1);
      contentOuterContainer.style.height = `${height}px`;

      if (runTime < HEIGHT_ANIMATION_DURATION_MS) {
        reqFrameId = window.requestAnimationFrame(animateContentHeight);
        return;
      }

      if (isWindowResized) return;

      showNewTabContent({ newActiveContentContainer });
    };

    // Call height animation function and skip classes and ARIA update
    if (isWindowResized) {
      reqFrameId = window.requestAnimationFrame(animateContentHeight);
      return;
    }

    const switchContent = (): void => {
      if (heightDifference !== 0) {
        reqFrameId = window.requestAnimationFrame(animateContentHeight);
        return;
      }

      // Switch classes without height animation
      showNewTabContent({ newActiveContentContainer });
    };

    /**
     * If animation is already processing, do not wait till its end
     * and call switchContent() immediately
     */
    if (!isSwitchingActive) {
      previousActiveContentContainer.addEventListener(
        "transitionend",
        () => {
          switchContent();
        },
        { once: true }
      );
    } else {
      switchContent();
    }

    // Proceed opacity to 0 transition on previous active content element
    previousActiveContentContainer.classList.remove(ACTIVE_CLASS);

    // ARIA update
    previousActiveContentContainer.setAttribute("aria-expanded", "false");
    newActiveContentContainer.setAttribute("aria-expanded", "true");
  };

  /**
   * Set active index helper var variable values
   */
  const setIndexVarHelpers = ({ newActiveIndex }: { newActiveIndex: number }): void => {
    previousActiveTabIndex = activeTabIndex;
    activeTabIndex = newActiveIndex;
  };

  /**
   * Update buttons classes and ARIA attributes
   * and call content switch function
   */
  const handleTabSwitch = (isInitialCheck = false): void => {
    if (activeTabIndex === previousActiveTabIndex && !isInitialCheck) return;

    isSwitchingActive = true;

    const activeButton = tabButtonArr[activeTabIndex];
    const previousActiveButton = tabButtonArr[previousActiveTabIndex];

    activeButton.classList.add(ACTIVE_CLASS);
    previousActiveButton.classList.remove(ACTIVE_CLASS);

    activeButton.setAttribute("aria-selected", "true");
    previousActiveButton.setAttribute("aria-selected", "false");

    handleContentSwitch({});
  };

  /**
   * Initial opened tab is tab on index 0.
   * So it is necessary to check if some tab button has equal
   * href as window.location hash and if so, scroll to the button
   */
  const detectStateFromUrl = (): void => {
    const { hash: urlHash } = window.location;

    if (!urlHash) return;

    let urlTabIndex = 0;

    const tabButtonFromHash = Array.from(tabButtonArr).find((button, index) => {
      const { hash: buttonHash } = button;

      if (!buttonHash) return false;

      urlTabIndex = index;

      return buttonHash === urlHash;
    });

    if (!tabButtonFromHash) return;

    setIndexVarHelpers({ newActiveIndex: urlTabIndex });
    handleTabSwitch();

    scroll.toElement({ target: tabButtonFromHash, customOffset: 10 });
  };

  /**
   * Handle tablist button click
   */
  const handleButtonClick = (event: MouseEvent): void => {
    // Prevent scroll jump on item
    event.preventDefault();

    container.querySelector(".js-tile")?.classList.add("is-inTab");

    let clickedTabIndex = 0;

    if (initialLoad) {
      initialLoad = false;
    }

    const clickedTabButton = Array.from(tabButtonArr).find((button, index) => {
      clickedTabIndex = index;
      return button === event.currentTarget;
    });

    if (clickedTabIndex === activeTabIndex) return;

    setIndexVarHelpers({ newActiveIndex: clickedTabIndex });

    // Set URL manually without page reload
    window.history.replaceState(null, null, clickedTabButton.href);
    handleTabSwitch();
  };

  /**
   * Detect initial open tab after page load
   */
  const detectInitialOpenTab = (): void => {
    const activeInitialIndex = Array.from(tabButtonArr).findIndex((button) => {
      return button.classList.contains(ACTIVE_CLASS);
    });

    activeTabIndex = activeInitialIndex;
  };

  /**
   * Some inner elments may contain height animations (Accordion), so it is important
   * to observe height animation of content containers and animate tabs height when
   * inner elements shrinks or grows
   */
  const listenToAccordionEventToggle = (): void => {
    const contentContainerArr = Array.from(tabContentContainerArr);

    contentContainerArr.forEach((contentContainerEl) => {
      /* Custom event from accordion */
      contentContainerEl.addEventListener("accordionToggle", (event: AccordionToggleEvent) => {
        const { additionalHeight } = event;

        handleContentSwitch({ isWindowResized: true, additionalHeight });
      });
    });
  };

  const prepareDefaultState = (): void => {
    // Apply click listener on every tab button
    tabButtonArr.forEach((button) => {
      button.addEventListener("click", handleButtonClick);
    });

    detectInitialOpenTab();

    // Check initial state from url
    detectStateFromUrl();

    // Scroll to opened tab in tablist, if needed
    handleActivePanelButtonVisibility({
      containerEl: tabButtonListEl,
      itemElement: tabButtonArr[activeTabIndex],
      callback: () => tabButtonListEl.classList.remove(TABLIST_INITIAL_CLASS),
    });

    listenToAccordionEventToggle();
  };

  // Allow Tab opening based on anchor click on page without page reload
  window.addEventListener("hashchange", () => {
    detectStateFromUrl();

    tabButtonListEl.classList.add(TABLIST_INITIAL_CLASS);

    handleActivePanelButtonVisibility({
      containerEl: tabButtonListEl,
      itemElement: tabButtonArr[activeTabIndex],
      callback: () => tabButtonListEl.classList.remove(TABLIST_INITIAL_CLASS),
    });
  });

  // Proceed height animation on resize event
  window.addEventListener(
    "resize",
    debounce(() => {
      handleContentSwitch({ isWindowResized: true });
    }, 200)
  );

  prepareDefaultState();
};

export default Tabs;
